import React from 'react';
import type { DateRange, FilterValueState, YearQuickSelect } from '../../table-data-detail.utils';
import { shouldShowGuidedText } from '../../table-data-detail.utils';
import { DateRangeFilterField } from './DateRangeFilterField';
import { DateSingleFilterField } from './DateSingleFilterField';
import { LookupFilterField } from './LookupFilterField';
import { NumericFilterField } from './NumericFilterField';
import type { MappedFilter, UpdateFilterState } from './types';

type FilterFieldRendererProps = {
  filter: MappedFilter;
  state?: FilterValueState;
  inputClassName: string;
  filtersDisabled: boolean;
  updateFilterState: UpdateFilterState;
  fetchFilterOptions: (filter: MappedFilter, query: string) => Promise<void> | void;
  yearQuickSelects: YearQuickSelect[];
  tableDateRange: DateRange | null;
  onYearQuickSelect: (range: DateRange, filterId: string) => void;
};

export const FilterFieldRenderer = React.memo(function FilterFieldRenderer({
  filter,
  state,
  inputClassName,
  filtersDisabled,
  updateFilterState,
  fetchFilterOptions,
  yearQuickSelects,
  tableDateRange,
  onYearQuickSelect,
}: FilterFieldRendererProps) {
  const label = filter.fieldData?.publicName ?? filter.fieldId;
  const showGuided = shouldShowGuidedText(filter, state);

  if (filter.filterType === 'date' && filter.matchType === 'range') {
    return (
      <DateRangeFilterField
        filter={filter}
        label={label}
        state={state}
        inputClassName={inputClassName}
        filtersDisabled={filtersDisabled}
        updateFilterState={updateFilterState}
        yearQuickSelects={yearQuickSelects}
        tableDateRange={tableDateRange}
        onYearQuickSelect={onYearQuickSelect}
        showGuidedText={showGuided}
      />
    );
  }

  if (filter.filterType === 'date') {
    return (
      <DateSingleFilterField
        filter={filter}
        label={label}
        state={state}
        inputClassName={inputClassName}
        filtersDisabled={filtersDisabled}
        updateFilterState={updateFilterState}
        showGuidedText={showGuided}
      />
    );
  }

  if (filter.filterType === 'numeric') {
    return (
      <NumericFilterField
        filter={filter}
        label={label}
        state={state}
        inputClassName={inputClassName}
        filtersDisabled={filtersDisabled}
        updateFilterState={updateFilterState}
        showGuidedText={showGuided}
      />
    );
  }

  return (
    <LookupFilterField
      filter={filter}
      label={label}
      state={state}
      inputClassName={inputClassName}
      filtersDisabled={filtersDisabled}
      updateFilterState={updateFilterState}
      fetchFilterOptions={fetchFilterOptions}
      showGuidedText={showGuided}
    />
  );
});
